import {
  CopyObjectCommand,
  CopyObjectCommandOutput,
  DeleteObjectCommand,
  GetObjectCommand,
  ListObjectsV2Command,
  ListObjectsV2CommandOutput,
  S3Client,
  _Object,
} from '@aws-sdk/client-s3';
import { isEmpty, isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, DocumentIcon, ExclamationIcon, FolderIcon, PlusIcon, XIcon } from '@heroicons/react/solid';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { FileAdder, months } from './FileAdder';
import { LambdaClient } from '@aws-sdk/client-lambda';

interface DocumentDisplayerProps {
  s3Client: S3Client;
  lambdaClient: LambdaClient;
  isAdmin: boolean;
  isPostCollege: boolean;
  isAdvisor: boolean;
}

export const DocumentDisplayer = (props: DocumentDisplayerProps) => {
  const [documentMap, setDocumentMap] = useState(new Map<string, string[]>());
  const [arePreCollegeDocsLoaded, setArePreCollegeDocsLoaded] = useState(false);
  const [arePostCollegeDocsLoaded, setArePostCollegeDocsLoaded] = useState(false);
  const [restricedMap, setRestrictedMap] = useState(new Map<string, boolean>());
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [showDeleteSpeedbump, setShowDeleteSpeedbump] = useState(false);
  const [fileToDelete, setFileToDelete] = useState('');
  const [openFolders, setOpenFolders] = useState<Set<string>>(new Set<string>());
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddFileModal, setShowAddFileModal] = useState(false);

  const getFiles = async () => {
    const restricedMapStart = new Map<string, boolean>();
    const documentMapStart = new Map<string, string[]>();
    const addToMap = (objects: _Object[], restricted: boolean) => {
      objects.forEach((entry) => {
        //Make sure we are dealing with files, not folders
        if (!isNil(entry.Key) && !isEmpty(entry.Key?.split('/')[1])) {
          const splitString = entry.Key?.split('/');
          const children = documentMapStart.get(splitString[0]);
          if (isNil(children)) {
            documentMapStart.set(splitString[0], [entry.Key]);
          } else {
            children.push(entry.Key);
            documentMapStart.set(splitString[0], children);
          }
          restricedMapStart.set(entry.Key, restricted);
        }
      });
    };

    const fetchBucket = async (bucket: string, restricted: boolean) => {
      let isTruncated = true;
      let continuationToken: string | undefined = undefined;
      while (isTruncated) {
        const output: ListObjectsV2CommandOutput = await props.s3Client.send(
          new ListObjectsV2Command({
            Bucket: bucket,
            ContinuationToken: continuationToken,
          })
        );
        if (!isNil(output.Contents)) {
          addToMap(output.Contents, restricted);
        }
        isTruncated = output.IsTruncated ?? false;
        continuationToken = output.NextContinuationToken;
      }
    };

    try {
      await fetchBucket('protected-mitchellfc-docs-everyone', false);
      setDocumentMap(new Map(documentMapStart));
      setRestrictedMap(new Map(restricedMapStart));
      setArePreCollegeDocsLoaded(true);
  
      if (props.isPostCollege || props.isAdvisor) {
        await fetchBucket('protected-mitchellfc-docs-post-grad', true);
        setDocumentMap(new Map(documentMapStart));
        setRestrictedMap(new Map(restricedMapStart));
        setArePostCollegeDocsLoaded(true);
      }
    } catch (e) {
      console.error("Failed to load documents from S3:", e);
      alert("There was an error loading your documents. Please check your connection and refresh.");
    }
  };

  useEffect(() => {
    if (!isNil(props.lambdaClient) && !isNil(props.s3Client)) {
      getFiles();
    }
  }, [props.lambdaClient, props.s3Client]);

  useEffect(() => {
    if (showAddFileModal || showDeleteSpeedbump) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showAddFileModal, showDeleteSpeedbump]);

  const openCloseFolder = (folder: string) => {
    if (openFolders.has(folder)) {
      openFolders.delete(folder);
    } else {
      openFolders.add(folder);
    }
    setOpenFolders(new Set(openFolders));
  };

  const getSortedKeys = () => {
    const keys = Array.from(documentMap.keys());
    const filteredKeys = keys.filter((key) => {
      const docs = documentMap.get(key) || [];
      return docs.some((doc) => doc.toLowerCase().includes(searchQuery.toLowerCase()));
    });

    filteredKeys.sort((a, b) => {
      const aWords = a.split(' ');
      const bWords = b.split(' ');
      if (aWords[0] === 'General' || aWords[0] === 'Myers') {
        return -1;
      }
      if (bWords[0] === 'General' || bWords[0] === 'Myers') {
        return 1;
      }
      const aYear = +aWords[1];
      const aMonth = aWords[0];
      const bYear = +bWords[1];
      const bMonth = bWords[0];

      if (aYear === bYear) {
        return months.indexOf(bMonth) - months.indexOf(aMonth);
      }
      return bYear - aYear;
    });

    return filteredKeys.map((key) => {
      const isOpen = openFolders.has(key) || searchQuery.trim().length > 0;
      const docs = documentMap.get(key)?.filter((doc) => doc.toLowerCase().includes(searchQuery.toLowerCase())).sort() || [];
      
      return (
      <div key={key} className="mb-4">
        <div onClick={() => openCloseFolder(key)} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 cursor-pointer hover:shadow-md transition-all">
          <div className="flex items-center dark:text-white text-slate-800">
            <FolderIcon className="h-6 w-6 text-blue-500 mr-3" />
            <span className="font-bold text-base sm:text-lg">{key}</span>
          </div>
          {isOpen ? (
            <ChevronUpIcon className="h-6 w-6 text-slate-400" />
          ) : (
            <ChevronDownIcon className="h-6 w-6 text-slate-400" />
          )}
        </div>
        {isOpen && (
          <ul className="mt-3 ml-4 md:ml-8 space-y-2">
            {docs.map((document) => (
                <li key={document} onClick={() => downloadFile(document)} className="group flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700 hover:shadow-sm transition-all cursor-pointer">
                  <div
                    className="flex items-center dark:text-white text-slate-800 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
                  >
                    <DocumentIcon className="h-5 w-5 text-slate-400 mr-3 shrink-0" />
                    <span className="truncate font-medium text-sm sm:text-base">{document.split('/')[1].split('.')[0]}</span>
                  </div>
                  {props.isAdmin && (
                    <div className="flex items-center mt-3 sm:mt-0 sm:ml-4 text-sm gap-4">
                      <label className="flex items-center cursor-pointer dark:text-slate-300 text-slate-600">
                        <input
                          className="form-checkbox h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500 mr-2"
                          type="checkbox"
                          checked={restricedMap.get(document)}
                          onChange={(event) => moveFile(event.target.checked, document)}
                        />
                        Restricted
                      </label>
                      <button
                        className="text-red-500 hover:text-red-700 hover:underline font-medium transition-colors"
                        onClick={() => {
                          setFileToDelete(document);
                          setShowDeleteSpeedbump(true);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </li>
              ))}
          </ul>
        )}
      </div>
      );
    });
  };

  const moveFile = (checked: boolean, fileName: string) => {
    setIsPageLoading(true);
    const originBucket = checked ? 'protected-mitchellfc-docs-everyone' : 'protected-mitchellfc-docs-post-grad';
    const newBucket = checked ? 'protected-mitchellfc-docs-post-grad' : 'protected-mitchellfc-docs-everyone';
    props.s3Client
      .send(
        new CopyObjectCommand({
          Bucket: newBucket,
          Key: fileName,
          CopySource: originBucket + '/' + fileName,
        })
      )
      .then((output: CopyObjectCommandOutput) => {
        console.info(output);
        deleteFile(fileName);
        getFiles();
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const deleteFile = (fileName?: string) => {
    const file = isNil(fileName) ? fileToDelete : fileName;
    setIsPageLoading(true);
    const bucket = restricedMap.get(file) ? 'protected-mitchellfc-docs-post-grad' : 'protected-mitchellfc-docs-everyone';
    props.s3Client
      .send(new DeleteObjectCommand({ Bucket: bucket, Key: file }))
      .then((output: CopyObjectCommandOutput) => {
        console.info(output);
        setIsPageLoading(false);
        setFileToDelete('');
        setShowDeleteSpeedbump(false);
        getFiles();
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const downloadFile = (fileName: string) => {
    const bucket = restricedMap.get(fileName) ? 'protected-mitchellfc-docs-post-grad' : 'protected-mitchellfc-docs-everyone';
    const getObjectCommand = new GetObjectCommand({
      Bucket: bucket,
      Key: fileName,
    });

    const new_tab = window.open() as Window;
    getSignedUrl(props.s3Client, getObjectCommand, { expiresIn: 3600 })
      .then((url) => {
        console.log(url);
        new_tab.location.href = url;
      })
      .catch(() => console.error('Error occured'));
  };

  const areAllDocumentsLoaded = () => {
    const userIsPreCollegeAndPreCollegeDocsAreLoaded = arePreCollegeDocsLoaded && !props.isPostCollege;
    const userIsPostCollegeAndAllDocsAreLoaded = arePreCollegeDocsLoaded && arePostCollegeDocsLoaded && props.isPostCollege;
    return (
      !isNil(props.lambdaClient) &&
      !isNil(props.s3Client) &&
      (userIsPreCollegeAndPreCollegeDocsAreLoaded || userIsPostCollegeAndAllDocsAreLoaded)
    );
  };

  return (
    <>
      {isPageLoading && (
        <div className="w-full h-full fixed block top-0 left-0 bg-slate-50 opacity-40 z-500">
          <div className="flex items-center justify-center h-full">
            <div className="w-16 h-16 border-b-2 border-slate-900 rounded-full animate-spin z-501"></div>
          </div>
        </div>
      )}
      {showDeleteSpeedbump && (
        <div className="block top-0 left-0 h-screen w-screen fixed items-center justify-center bg-slate-900/60 backdrop-blur-sm z-[100]" onClick={() => setShowDeleteSpeedbump(false)}>
          <div className="flex items-center justify-center h-full">
            <div className="inline-block justify-center items-center align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" onClick={(e) => e.stopPropagation()}>
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    Delete File
                    <div className="mt-2">
                      <p className="text-sm text-slate-500">{`Are you sure you want to delete ${fileToDelete}?`}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => deleteFile()}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-xl border border-slate-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowDeleteSpeedbump(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAddFileModal && (
        <div className="block top-0 left-0 h-screen w-screen fixed items-center justify-center bg-slate-900/60 backdrop-blur-sm z-[100]" onClick={() => setShowAddFileModal(false)}>
          <div className="flex items-center justify-center h-full p-4">
            <div className="w-full max-h-[90vh] flex flex-col bg-white dark:bg-slate-800 rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:max-w-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 sm:p-6 overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-extrabold tracking-tight dark:text-white text-slate-900">Upload New Document</h3>
                  <button onClick={() => setShowAddFileModal(false)} className="text-slate-400 hover:text-slate-500 transition-colors">
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <FileAdder lambdaClient={props.lambdaClient as LambdaClient} getFiles={async () => { await getFiles(); setShowAddFileModal(false); }} s3Client={props.s3Client as S3Client} />
              </div>
            </div>
          </div>
        </div>
      )}
      <h1 className="mb-6 text-2xl sm:text-4xl font-extrabold tracking-tight dark:text-white text-slate-900">
        Documents
      </h1>
      {!areAllDocumentsLoaded() ? (
        <div className="space-y-4 animate-pulse mb-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-14 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700"></div>
          ))}
        </div>
      ) : (
        <>
          {props.isAdmin && (
            <div className="flex justify-start mb-6">
                <button
                  onClick={() => setShowAddFileModal(true)}
                  className="inline-flex items-center justify-center rounded-xl px-5 py-2.5 bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 transition-all shadow-sm hover:shadow-md"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Upload Documents
                </button>
            </div>
          )}
          <div className="mb-6 relative">
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full p-4 pl-12 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all shadow-sm"
            />
            <svg className="w-6 h-6 absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {getSortedKeys()}
        </>
      )}
    </>
  );
};
